import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { getQueryKey } from '@/api/utils/getQueryKey';
import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { QUERY_KEY as QUERY_KEY_GET_ALL } from './useGetPayments';

export interface CancelRentSubscriptionRequest {
  subscriptionId: string;
  reason?: string;
  tenant_id?: string; // Optional tenant_id for property managers
}

export interface CancelRentSubscriptionResponse {
  success: boolean;
  message: string;
  subscriptionId: string;
}

const METHOD = 'POST';
const ENDPOINT = 'payments/cancel-subscription';
export const QUERY_KEY = (params?: CancelRentSubscriptionRequest) =>
  getQueryKey(METHOD, ENDPOINT, params);

export const useCancelRentSubscription = (
  stateFns?: MutationStateFns<CancelRentSubscriptionResponse>
) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CancelRentSubscriptionRequest) => {
      if (!session?.access_token) {
        throw new Error('Authentication required');
      }

      // Call the backend API to cancel the subscription
      const response = await handleApiCall<
        CancelRentSubscriptionResponse,
        CancelRentSubscriptionRequest
      >(session, METHOD, getResolvedEndpoint(ENDPOINT, {}), data);

      return response;
    },
    onSuccess: data => {
      enqueueSnackbar('Rent subscription cancelled successfully', { variant: 'success' });

      // Invalidate payments query to refresh the list
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_GET_ALL(),
      });

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar(
        (err as AxiosError<{ message: string }>)?.response?.data?.message ||
          'Failed to cancel rent subscription. Please try again.',
        { variant: 'error' }
      );

      stateFns?.onError?.(err);
    },
  });
};
